/**
 * useAISetup Hooks
 *
 * React Query mutations for the AI Application Setup feature.
 * - useAISetupValidate: Pre-creation validation (app code + repo uniqueness)
 * - useAISetup: Full setup execution (create app, token, repo, push)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { aiSetupService } from '../../../api/ai-setup.service';
import type {
  TemplateInfo,
  AISetupValidateDTO,
  AISetupValidateResponse,
  AISetupDTO,
  AISetupResponse,
} from '../../../types/ai-setup.types';

// ============================================================================
// TEMPLATES HOOK
// ============================================================================

interface UseTemplatesOptions {
  tenantId: string;
  enabled?: boolean;
}

export const useTemplates = (options: UseTemplatesOptions) => {
  const { tenantId, enabled = true } = options;

  return useQuery<TemplateInfo[], Error>({
    queryKey: ['ai-templates', tenantId],
    queryFn: () => aiSetupService.getTemplates(tenantId),
    enabled: !!tenantId && enabled,
  });
};

// ============================================================================
// VALIDATE HOOK
// ============================================================================

interface UseAISetupValidateOptions {
  tenantId: string;
  onSuccess?: (data: AISetupValidateResponse) => void;
  onError?: (error: Error) => void;
}

export const useAISetupValidate = (options: UseAISetupValidateOptions) => {
  const { tenantId, onSuccess, onError } = options;

  return useMutation<AISetupValidateResponse, Error, AISetupValidateDTO>({
    mutationFn: (data) => aiSetupService.validate(tenantId, data),
    onSuccess,
    onError,
  });
};

// ============================================================================
// SETUP HOOK
// ============================================================================

interface UseAISetupOptions {
  tenantId: string;
  onSuccess?: (data: AISetupResponse) => void;
  onError?: (error: Error) => void;
}

export const useAISetup = (options: UseAISetupOptions) => {
  const { tenantId, onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation<AISetupResponse, Error, AISetupDTO>({
    mutationFn: (data) => aiSetupService.setup(tenantId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['applications', tenantId] });
      onSuccess?.(data);
    },
    onError,
  });
};
